'use client';

import Link from '@eliadmin/core/src/components/link';
import { useState, useRef } from 'react';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CategoryCard } from '../categories/category-card';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { MdKeyboardArrowDown } from 'react-icons/md';

export function Categories({
  categories,
  className,
}: {
  categories: CategoriesEntity[];
  className?: { container?: string };
}) {
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (slug: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setHoveredCategory(slug);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setHoveredCategory(null);
    }, 150);
  };

  const handleLinkClick = () => {
    setHoveredCategory(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  return (
    <div
      className={cn(
        'sticky top-0 z-50 flex w-full items-center justify-center py-6',
        'ea-categories__container',
        className?.container,
      )}
    >
      <div
        className={cn(
          'custom-container',
          'relative flex items-center justify-center gap-6 lg:gap-8',
        )}
      >
        {categories.map(category => {
          const hasChildren = category.childs && category.childs.length > 0;
          const isHovered = hoveredCategory === category.slug;

          return (
            <div
              key={category.slug}
              onMouseEnter={() => handleMouseEnter(category.slug)}
              onMouseLeave={handleMouseLeave}
            >
              <Link
                prefetch={false}
                href={category.slug}
                className={cn(
                  'ea-categories__category-card',
                  'flex items-center justify-center gap-2 px-4 py-3 text-base font-medium text-white xl:text-lg',
                  'hover:bg-white/5',
                  'border-b-2 border-b-transparent hover:border-b-primary',
                )}
                onClick={handleLinkClick}
              >
                {category.name}
                {hasChildren && (
                  <MdKeyboardArrowDown
                    size={20}
                    className={cn(
                      'text-white transition-all',
                      isHovered ? 'rotate-180' : 'rotate-0',
                    )}
                  />
                )}
              </Link>

              {hasChildren && (
                <div
                  className={cn(
                    'absolute left-0 top-full z-50 mt-4 w-full bg-white',
                    'transform transition-all duration-300 ease-in-out',
                    isHovered
                      ? 'pointer-events-auto translate-y-0 opacity-100'
                      : 'pointer-events-none -translate-y-2 opacity-0',
                  )}
                >
                  <div className="grid w-full grid-cols-4 gap-4 p-12 xl:gap-6">
                    {category.childs &&
                      category.childs.map(child => (
                        <CategoryCard
                          key={child.slug}
                          category={child}
                          onClick={handleLinkClick}
                          detailedStyles={true}
                        />
                      ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
